import React from "react";
import { useFormContext } from "react-hook-form";
import FormErrorMessage from "Components/Shared/FormComponents/FormErrorMessage/FormErrorMessage";
import pretty from "Shared/pretty";
import { TextFieldProps } from "@material-ui/core";
import TextFieldWrapper from "../TextFieldWrapper/TextFieldWrapper";


export interface AmountFieldProps {
  name: string;
  maxAmount?: number;
  errorMessages?: {
    required?: string;
    monetary?: string;
    range?: string;
  };
  textFieldProps?: TextFieldProps;
}

function withDefaultErrorMessages(props: AmountFieldProps): AmountFieldProps {
  const defaultErrorMessages = {
    required: "Please provide an amount",
    monetary: "Please enter a valid monetary amount, e.g. 5.30",
    range: `Amount cannot be over ${pretty.dollar(props.maxAmount)}`,
  };


  return {
    name: props.name,
    maxAmount: props.maxAmount,
    errorMessages: {...defaultErrorMessages, ...props.errorMessages },
  };
}

const AmountField = (props: AmountFieldProps) => {
  const moneyRegex = /^[0-9]+(\.[0-9]{1,2})?$/;
  const { register, errors } = useFormContext();
  const options = withDefaultErrorMessages(props);

  const errorMessage = React.useCallback(() => {
    if (!errors[props.name]) {
      return "";
    }

    const errorMessages = options.errorMessages;
    const type = errors[props.name].type as keyof typeof errorMessages;
    return errorMessages![type];
  }, [errors, props.name, options.errorMessages]);


  return (
    <>
      <TextFieldWrapper
        name={props.name}
        placeholder={`Amount up to ${pretty.dollar(props.maxAmount)}`}
        type={"number"}
        inputProps={{ min: 0 }}
        inputRef={register({
          required: true,
          validate: {
            monetary: value => !!value.match(moneyRegex),
            range: value => typeof props.maxAmount === "number" && value > 0 && value <= props.maxAmount / 100,
          }
        })}
        {...props.textFieldProps}
      />
      <FormErrorMessage
        showError={!!errors[props.name]}
        labelFor={props.name}
        message={errorMessage()}
      />
    </>
  );
};

export default AmountField;
